"use client";

import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import DocumentsPage from "./DocumentsPage";

type SharePointDoc = {
  name: string;
  url: string;
  reference?: string;
};

type AssignDocumentData = {
  data: {
    [fileName: string]: string[];
  };
};

// Wrapper component that filters documents and uses DocumentsPage display
export default function AssignedDocumentsPage() {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [filteredDocs, setFilteredDocs] = useState<SharePointDoc[]>([]);
  const [assignDocumentData, setAssignDocumentData] = useState<AssignDocumentData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadAssignDocumentData = async () => {
    try {
      const res = await (window as any)?.sharePointAPI?.downloadAssignDocumentJson?.();
      if (res?.success) {
        setAssignDocumentData(res.data || { data: {} });
      } else {
        setAssignDocumentData({ data: {} });
      }
    } catch (e: any) {
      console.error("Error loading assign document data:", e);
      setAssignDocumentData({ data: {} });
    }
  };

  const loadAndFilterDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await (window as any)?.sharePointAPI?.loadAllDocuments?.();
      if (res?.success) {
        const allDocs = Array.isArray(res.data) ? res.data : [];
        
        // Filter documents where current user is assigned
        // Only show files that exist in the JSON where user_name matches
        if (user?.user_name && assignDocumentData?.data) {
          // First, deduplicate allDocs by name to avoid duplicates
          const uniqueDocsMap = new Map<string, SharePointDoc>();
          allDocs.forEach((doc: SharePointDoc) => {
            if (doc.name && !uniqueDocsMap.has(doc.name)) {
              uniqueDocsMap.set(doc.name, doc);
            }
          });
          const uniqueDocs = Array.from(uniqueDocsMap.values());
          
          // Filter documents where current user is assigned
          const filtered = uniqueDocs.filter((doc: SharePointDoc) => {
            // Check if this file name exists in the assign document data
            const assignedUsers = assignDocumentData.data[doc.name];
            if (Array.isArray(assignedUsers)) {
              // Check if current user's user_name is in the array (exact match)
              const isAssigned = assignedUsers.includes(user.user_name);
              return isAssigned;
            }
            return false;
          });
          
          // Ensure final list is also unique (double check)
          const finalUniqueMap = new Map<string, SharePointDoc>();
          filtered.forEach((doc: SharePointDoc) => {
            if (doc.name && !finalUniqueMap.has(doc.name)) {
              finalUniqueMap.set(doc.name, doc);
            }
          });
          
          setFilteredDocs(Array.from(finalUniqueMap.values()));
        } else {
          // If no user or no assign data, show empty
          setFilteredDocs([]);
        }
      } else {
        setFilteredDocs([]);
      }
    } catch (e: any) {
      console.error("Error loading documents:", e);
      setFilteredDocs([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.user_name, assignDocumentData]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await loadAssignDocumentData();
    };
    run();
    return () => {
      mounted = false;
    };
  }, [currentEngagement, user]);

  // Reload when assign document data changes
  useEffect(() => {
    if (assignDocumentData) {
      loadAndFilterDocuments();
    }
  }, [assignDocumentData, loadAndFilterDocuments]);

  // Use DocumentsPage as a wrapper with filtered documents
  return (
    <DocumentsPage 
      filteredDocs={filteredDocs} 
      isLoading={isLoading} 
      title="Assigned Documents"
      description="Documents assigned to you"
      showUploadButton={false}
    />
  );
}

